import React from 'react';
import { useState } from 'preact/hooks';
import { Controller, useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Checkbox, Form, Input, Modal, Space, Button } from 'antd';
import { TextLarge, ButtonTheme } from '../../../components';
import { ResponsivePageLayout } from '../../../components/page-layout/ResponsivePageLayout';
import { ContainerButton } from '../../../styles/global-style';
import { StyledContainer } from '../claim-approve/styles';
import { columns as approveColumns } from '../claim-approve/columns';
import { createColumnText, widthOptions } from '../../../components/table/function/createColumn';
import { useResponsive } from '../../../hooks/useResponsive';
import {
  GET,
  POST,
  GET_CLAIM_RESEND_WAIT_APPROVE,
  POST_CLAIM_RESEND_APPROVE,
  POST_CLAIM_RESEND_REJECT,
} from '../../../services';
import {
  rowsFromResendApproveResponse,
  showCsSendResult,
} from '../../../components/cs-send-result';
import { openAlert } from '../../../components/alert/useAlert';
import { CustomerHistoryModal } from '../../../components/customer-history';
import { StyledStockTable } from '../claim-product/styles';
import { useStockAvailability } from '../claim-product/hooks/useStockAvailability';
import { STOCK_ROW_CLASS } from '../claim-product/resource/stockConst';
import { ApprovalFilters } from '../shared/ApprovalFilters';
import {
  APPROVAL_STOCK_ALL,
  countApprovalSelectedByItem,
  filterApprovalRowsByStock,
  getApprovalStockState,
} from '../shared/approvalStock';

const PAGE_SIZE = 20;
const EMPTY_ROWS = [];

export const ClaimResendApprove = () => {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [selectedIds, setSelectedIds] = useState([]);
  const [stockStatus, setStockStatus] = useState(APPROVAL_STOCK_ALL);
  const [action, setAction] = useState(null);
  const [historyTarget, setHistoryTarget] = useState(null);
  const { control, reset, handleSubmit } = useForm({ defaultValues: { remark: '' } });
  const { isMobile } = useResponsive();
  const queryClient = useQueryClient();
  const [modal, modalContext] = Modal.useModal();
  const list = useQuery({
    queryKey: ['claim-resend-wait-approve', search, page],
    queryFn: async () => {
      const response = await GET(GET_CLAIM_RESEND_WAIT_APPROVE({ limit: PAGE_SIZE, page, search }));
      return response?.result?.items || [];
    },
  });
  const dataSource = list.data || EMPTY_ROWS;
  const { stockMap, loading: stockLoading } = useStockAvailability(dataSource);
  const selectedByItem = countApprovalSelectedByItem(dataSource, selectedIds, 'requestId');
  const filteredRows = filterApprovalRowsByStock(dataSource, stockStatus, stockMap, selectedByItem);
  const mutation = useMutation({
    mutationFn: async (values) => {
      const response = await POST(
        action.type === 'reject' ? POST_CLAIM_RESEND_REJECT() : POST_CLAIM_RESEND_APPROVE(),
        action.type === 'reject'
          ? { requestId: action.requestId, remark: values.remark.trim() }
          : { requestIdList: selectedIds, remark: values.remark.trim() },
      );
      if (response?.success === false) throw new Error(response.message);
      return response;
    },
    onSuccess: (response) => {
      if (action.type === 'approve') {
        showCsSendResult(modal, {
          title: 'ผลการอนุมัติส่งซ้ำ',
          rows: rowsFromResendApproveResponse(response),
        });
      } else {
        openAlert({ model: 'message', type: 'success', message: 'ไม่อนุมัติคำขอส่งซ้ำแล้ว' });
      }
      setAction(null);
      setSelectedIds([]);
      queryClient.invalidateQueries({ queryKey: ['claim-resend-wait-approve'] });
    },
  });
  const openAction = (value) => {
    reset({ remark: '' });
    setAction(value);
  };
  // approveColumns ต่อ claimInfoColumns ของ shared ให้เองอยู่แล้ว ไอคอน ClaimCountIcon ก่อนเลขที่ลงทะเบียน
  // จึงแสดงที่นี่โดยไม่ต้องเรียกใช้ตรง ๆ
  const columns = approveColumns({
    onReject: (record) => openAction({ type: 'reject', requestId: record.requestId }),
    onShowHistory: (record) => setHistoryTarget(record),
    stockMap,
    selectedByItem,
  });
  columns.splice(
    columns.length - 1,
    0,
    createColumnText('เหตุผลส่งซ้ำ', 'reason', widthOptions(250)),
    createColumnText('ผู้ขอ', 'createBy', widthOptions(160)),
    createColumnText('วันที่ขอ', 'createDate', widthOptions(180)),
    createColumnText('เลข CS', 'refCsId', widthOptions(160)),
    createColumnText('เลข IJ', 'refJournalId', widthOptions(160)),
  );
  if (isMobile) {
    columns.unshift({
      title: 'เลือก',
      dataIndex: 'requestId',
      render: (id) => (
        <Checkbox
          checked={selectedIds.includes(id)}
          onChange={() =>
            setSelectedIds((prev) =>
              prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id],
            )
          }
        />
      ),
    });
  }
  const changePage = (next) => {
    setSelectedIds([]);
    setPage(next);
  };
  const onSearch = ({
    search: nextSearch,
    stockStatus: nextStockStatus,
    reset: clearSelection,
  }) => {
    if (nextSearch !== search || clearSelection) {
      setSelectedIds([]);
      setPage(0);
    }
    setStockStatus(nextStockStatus);
    setSearch(nextSearch);
  };

  return (
    <StyledContainer>
      <TextLarge text="อนุมัติส่งซ้ำ" />
      <ApprovalFilters onSearch={onSearch} />
      <StyledStockTable>
        <ResponsivePageLayout
          tableLayout={{
            columns,
            dataSource: filteredRows,
            loading: list.isFetching || stockLoading,
            rowKey: 'requestId',
            rowSelection: { selectedRowKeys: selectedIds, onChange: setSelectedIds },
            rowClassName: (record) => {
              const { state } = getApprovalStockState(record, stockMap, selectedByItem);
              return STOCK_ROW_CLASS[state] ?? '';
            },
            extraContent: (
              <ContainerButton right>
                <ButtonTheme
                  useFor="CONFIRM"
                  title="อนุมัติ"
                  disabled={!selectedIds.length || mutation.isPending || list.isFetching}
                  onClick={() => openAction({ type: 'approve' })}
                />
              </ContainerButton>
            ),
          }}
        />
      </StyledStockTable>
      <Space style={{ marginTop: 16 }}>
        <Button disabled={page === 0 || list.isFetching} onClick={() => changePage(page - 1)}>
          ก่อนหน้า
        </Button>
        <span>หน้า {page + 1}</span>
        <Button
          disabled={dataSource.length < PAGE_SIZE || list.isFetching}
          onClick={() => changePage(page + 1)}
        >
          ถัดไป
        </Button>
      </Space>
      {action && (
        <Modal
          title={action.type === 'reject' ? 'ไม่อนุมัติส่งซ้ำ' : 'อนุมัติส่งซ้ำ'}
          open
          okText="ยืนยัน"
          cancelText="ยกเลิก"
          confirmLoading={mutation.isPending}
          cancelButtonProps={{ disabled: mutation.isPending }}
          closable={!mutation.isPending}
          maskClosable={false}
          onCancel={() => {
            if (!mutation.isPending) setAction(null);
          }}
          onOk={handleSubmit((values) => {
            if (!mutation.isPending) mutation.mutate(values);
          })}
        >
          {action.type === 'approve' && <p>ยืนยันอนุมัติส่งซ้ำ {selectedIds.length} รายการ</p>}
          <Controller
            name="remark"
            control={control}
            rules={{
              validate: (value) =>
                action.type !== 'reject' || !!value.trim() || 'กรุณาระบุหมายเหตุ',
              maxLength: { value: 250, message: 'ระบุได้ไม่เกิน 250 ตัวอักษร' },
            }}
            render={({ field, fieldState }) => (
              <Form.Item
                label="หมายเหตุ"
                required={action.type === 'reject'}
                validateStatus={fieldState.error ? 'error' : undefined}
                help={fieldState.error?.message}
              >
                <Input.TextArea
                  {...field}
                  rows={4}
                  maxLength={250}
                  showCount
                  disabled={mutation.isPending}
                />
              </Form.Item>
            )}
          />
        </Modal>
      )}
      {historyTarget && (
        <CustomerHistoryModal
          open
          onToggle={() => setHistoryTarget(null)}
          initialValues={historyTarget}
        />
      )}
      {modalContext}
    </StyledContainer>
  );
};
